/**
 * `createApiRunsSource` — the `pipeline-mgmt-api` *run jobs* port over the shared
 * `ApiClient`.
 *
 * A run job is the async, monitorable batch path: it describes WHERE a batch of
 * transactions comes from (an ingest `source`) and runs each one through a
 * pipeline. Because a real ingest ("the latest N txs involving this address", a
 * set of fixed tx hashes) runs far longer than API Gateway's request edge allows,
 * the backend records the job + enqueues it; an SQS worker drains it and rolls
 * each tx's outcome back onto the job. This adapter exposes a camelCase domain
 * and a `poll(id)` helper the run console calls on an interval until the job
 * reaches a terminal state. Canonical `/runs/*` paths are mapped to
 * `private/pipeline-mgmt-api` by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { type BodyScope } from "@authe/fe-commons/pipeline";

/** Where a run job sources its transactions (mirrors backend `RunSourceKind`). */
export type RunSourceKind =
  | "inline"
  | "latest"
  | "tx_hashes"
  | "tx_specs"
  | "block_range"
  | "pair_swaps"
  | "token_transfers";

/**
 * Event kinds a `pair_swaps` run can be narrowed to (mirrors the provider's
 * `PairSwapTransactionType`). Spellings are case-sensitive on the wire.
 */
export const PAIR_SWAP_TRANSACTION_TYPES = [
  "buy",
  "sell",
  "addLiquidity",
  "removeLiquidity",
] as const;

export type PairSwapTransactionType = (typeof PAIR_SWAP_TRANSACTION_TYPES)[number];

/** One leg of a multi-chain `tx_specs` ingest: a tx hash on a specific chain. */
export interface RunTxSpec {
  chainId: string;
  txHash: string;
}

/** Lifecycle of a run job (mirrors backend `RunJobStatus`). */
export type RunJobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

/** A run job is finished once it reaches one of these (stop polling). */
export const TERMINAL_RUN_STATUSES: ReadonlySet<RunJobStatus> = new Set<RunJobStatus>([
  "succeeded",
  "failed",
  "cancelled",
]);

export function isTerminalRunStatus(status: RunJobStatus): boolean {
  return TERMINAL_RUN_STATUSES.has(status);
}

/**
 * Lifecycle rank — a run only ever moves forward: queued (0) → running (1) →
 * terminal (2). Used to reject stale reads that would regress a job.
 */
export function runStatusRank(status: RunJobStatus): number {
  switch (status) {
    case "queued":
      return 0;
    case "running":
      return 1;
    default:
      return 2; // succeeded | failed | cancelled
  }
}

/**
 * Reconcile two views of the SAME run, keeping status monotonic.
 *
 * `pipeline-mgmt-api` runs on a multi-region DynamoDB global table, so reads can
 * land on a replica that hasn't caught up to the worker's last write — making a
 * finished run momentarily read back as `queued`/`running`. We treat a
 * lower-ranked incoming status as a stale replica read and keep the more
 * advanced view; monotonic counters (and a once-known total) likewise never
 * regress. This stops the run list from "flapping" between statuses.
 */
export function mergeRunJob(prev: RunJobSummary | undefined, next: RunJobSummary): RunJobSummary {
  if (!prev || prev.id !== next.id) return next;
  if (runStatusRank(next.status) < runStatusRank(prev.status)) return prev;
  return {
    ...next,
    totalCount: Math.max(next.totalCount, prev.totalCount),
    processedCount: Math.max(next.processedCount, prev.processedCount),
    failedCount: Math.max(next.failedCount, prev.failedCount),
    producedTx: next.producedTx.length >= prev.producedTx.length ? next.producedTx : prev.producedTx,
    finishedAt: next.finishedAt ?? prev.finishedAt,
    error: next.error ?? prev.error,
    // Written once by the worker at the end of ingest — a stale replica read
    // that predates that write must not blank the only account of why the run
    // found nothing.
    ingestReport: next.ingestReport ?? prev.ingestReport,
    // Immutable submit-time provenance — never let a lagging/partial read blank it.
    deploymentColour: next.deploymentColour ?? prev.deploymentColour,
    applicationVersion: next.applicationVersion ?? prev.applicationVersion,
    qaConfig: next.qaConfig ?? prev.qaConfig,
  };
}

/** A single transaction's outcome within a run (the run console renders + links from these). */
export interface ProducedTx {
  pipelineRunId?: string | null;
  canonicalTxId?: string | null;
  txHash?: string | null;
  status: string;
  error?: string | null;
}

export interface RunJobSummary {
  id: string;
  pipelineId: string;
  sourceKind: RunSourceKind;
  chainId?: string | null;
  provider: string;
  count: number;
  /**
   * The address the ingest was scoped to: the contract/wallet for `latest` and
   * `block_range`, or the DEX pool for `pair_swaps`.
   */
  address?: string | null;
  selector?: string | null;
  onlyMatchingSelector: boolean;
  /** `pair_swaps`: the comma-separated event kinds the pair feed was filtered to. */
  transactionTypes?: string | null;
  txHashes: string[];
  /** `tx_specs`: the multi-chain `{chainId, txHash}` legs (cross-chain grouping). */
  txSpecs: RunTxSpec[];
  fromBlock?: number | null;
  toBlock?: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  /** Whether the window above was a request or a fallback — see `RunSourceInput`. */
  windowMode?: "exact" | "only_when_large" | null;
  targetTier?: string | null;
  storeBody: boolean;
  /** Per-run override of the body storage scope (`customer`/`org`/`tenant`/`user`); null inherits the pipeline's. */
  bodyScope?: BodyScope | null;
  /** Force-reprocess: re-ran every step ignoring the idempotency cache and overwrote stored rows/bodies. */
  overwrite: boolean;
  status: RunJobStatus;
  requestedBy?: string | null;
  startedAt?: number | null;
  finishedAt?: number | null;
  createdAt?: number | null;
  totalCount: number;
  processedCount: number;
  failedCount: number;
  error?: string | null;
  /**
   * Blue/green lane this run's child runs executed on: `blue` (latest deploy —
   * a QA/blue caller) or `green` (live release). `null` on runs recorded before
   * this was tracked. The console badges a blue run so it's clear it didn't run
   * on the live green release.
   */
  deploymentColour?: string | null;
  /** Published Lambda version of the edge that enqueued this run (build provenance). */
  applicationVersion?: string | null;
  /** Snapshot of the caller's blue/green routing config at submit time — present only for QA runs. */
  qaConfig?: Record<string, unknown> | null;
  /** Only populated on the single-job (`get`/`poll`) fetch, not the list. */
  producedTx: ProducedTx[];
  /**
   * What the ingest actually resolved, and why it produced nothing when it did.
   *
   * A run that fetches zero transactions SUCCEEDS, so `status` and `error` say
   * nothing about it and the counts say only "0". This is the difference
   * between a correct empty run and a window that misses the activity — which
   * are the same row, and opposite next actions.
   */
  ingestReport: IngestReport | null;
}

/** The ingest's own account of a run. Every field optional by source kind. */
export interface IngestReport {
  /** The blocks the dates resolved to — the range actually queried. */
  resolvedFromBlock: number | null;
  resolvedToBlock: number | null;
  rows: number | null;
  /** A sentence naming why the result was empty. Absent when it was not. */
  emptyReason: string | null;
  /** When the address was last active on this chain, if the reason needed it. */
  lastActivityBlock: number | null;
  lastActivityAt: string | null;
}

function ingestReportFromWire(raw: unknown): IngestReport | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  const text = (v: unknown): string | null =>
    typeof v === "string" && v.trim() ? v : null;
  const report: IngestReport = {
    resolvedFromBlock: num(r.resolved_from_block),
    resolvedToBlock: num(r.resolved_to_block),
    rows: num(r.rows),
    emptyReason: text(r.empty_reason),
    lastActivityBlock: num(r.last_activity_block),
    lastActivityAt: text(r.last_activity_at),
  };
  // An all-empty report is the same as none, and rendering a heading over
  // nothing would imply the ingest declined to answer rather than that this run
  // predates the field.
  return Object.values(report).some((v) => v !== null) ? report : null;
}

/** The ingest `source` half of a create request. */
export interface RunSourceInput {
  kind: RunSourceKind;
  chainId?: string | null;
  provider?: string;
  count?: number;
  /** `inline`: verbose tx payloads to process verbatim (the fixed-tx test path). */
  payloads?: Record<string, unknown>[];
  /**
   * `latest` / `block_range`: discover the txs involving this address (either
   * side). `pair_swaps`: the DEX pool whose trade feed to pull.
   * `token_transfers`: the ERC-20 CONTRACT whose transfer events to pull — a
   * token is not a party to the transfers of its own supply, so the
   * address-scoped kinds see only the direct calls it receives.
   */
  address?: string | null;
  selector?: string | null;
  onlyMatchingSelector?: boolean;
  /**
   * `pair_swaps`: narrow the pair feed to a comma-separated subset of
   * {@link PAIR_SWAP_TRANSACTION_TYPES}. Omit for every kind.
   */
  transactionTypes?: string | null;
  /** `tx_hashes`: the explicit hashes to fetch (all on `chainId`). */
  txHashes?: string[];
  /** `tx_specs`: explicit `{chainId, txHash}` pairs across chains (cross-chain grouping). */
  txSpecs?: RunTxSpec[];
  /** `block_range`: the inclusive window, by block number… */
  fromBlock?: number | null;
  toBlock?: number | null;
  /** …or by ISO date/timestamp (resolved to blocks via `dateToBlock`). */
  fromDate?: string | null;
  toDate?: string | null;
  /**
   * What the window above MEANS.
   *
   * `exact` (the default, and what a deliberate block-range backfill wants)
   * fetches those blocks. `only_when_large` treats the window as a bound on cost
   * rather than a filter on relevance: the ingest asks how many transactions the
   * address has, and under the run's own `count` cap it drops the window and
   * takes the whole history — cheaper, and it cannot miss the period by a
   * boundary. Over the cap the window is applied, which is where it earns its
   * keep: an exchange hot wallet has 30M transactions and "the newest 100" of
   * those never reaches an older stretch.
   */
  windowMode?: "exact" | "only_when_large" | null;
}

export interface CreateRunInput {
  pipelineId?: string;
  source: RunSourceInput;
  targetTier?: string | null;
  storeBody?: boolean;
  /**
   * Per-run override of where each transaction body is stored
   * (`customer`/`org`/`tenant`/`user`). Omitted/null inherits the pipeline's
   * authored scope (which defaults to `tenant`).
   */
  bodyScope?: BodyScope | null;
  /** Force-reprocess: re-run every step ignoring the idempotency cache and overwrite stored rows/bodies. */
  overwrite?: boolean;
}

export interface RunJobPage {
  items: RunJobSummary[];
  nextCursor: string | null;
}

export interface ListRunsQuery {
  pipelineId?: string;
  cursor?: string | null;
  limit?: number;
}

export interface RunsSource {
  list(query?: ListRunsQuery): Promise<RunJobPage>;
  get(id: string): Promise<RunJobSummary>;
  create(input: CreateRunInput): Promise<RunJobSummary>;
  /** Convenience alias of `get` for the polling loop (semantic clarity at call sites). */
  poll(id: string): Promise<RunJobSummary>;
}

function producedFromWire(raw: Record<string, any>): ProducedTx {
  return {
    pipelineRunId: raw.pipeline_run_id ?? null,
    canonicalTxId: raw.canonical_tx_id ?? null,
    txHash: raw.tx_hash ?? null,
    status: String(raw.status ?? "ok"),
    error: raw.error ?? null,
  };
}

function jobFromWire(raw: Record<string, any>): RunJobSummary {
  return {
    id: raw.id,
    pipelineId: String(raw.pipeline_id ?? ""),
    sourceKind: (raw.source_kind ?? "inline") as RunSourceKind,
    chainId: raw.chain_id ?? null,
    provider: String(raw.provider ?? "moralis"),
    count: Number(raw.count ?? 0),
    address: raw.address ?? null,
    selector: raw.selector ?? null,
    onlyMatchingSelector: Boolean(raw.only_matching_selector ?? true),
    transactionTypes: raw.transaction_types ?? null,
    txHashes: (raw.tx_hashes ?? []) as string[],
    txSpecs: ((raw.tx_specs ?? []) as Record<string, any>[]).map((s) => ({
      chainId: String(s.chain_id ?? s.chainId ?? ""),
      txHash: String(s.tx_hash ?? s.txHash ?? ""),
    })),
    fromBlock: raw.from_block ?? null,
    toBlock: raw.to_block ?? null,
    fromDate: raw.from_date ?? null,
    toDate: raw.to_date ?? null,
    windowMode: (raw.window_mode ?? null) as RunJobSummary["windowMode"],
    targetTier: raw.target_tier ?? null,
    storeBody: Boolean(raw.store_body ?? true),
    bodyScope: (raw.body_scope ?? null) as BodyScope | null,
    overwrite: Boolean(raw.overwrite ?? false),
    status: (raw.status ?? "queued") as RunJobStatus,
    requestedBy: raw.requested_by ?? null,
    startedAt: raw.started_at ?? null,
    finishedAt: raw.finished_at ?? null,
    createdAt: raw.created_at ?? null,
    totalCount: Number(raw.total_count ?? 0),
    processedCount: Number(raw.processed_count ?? 0),
    failedCount: Number(raw.failed_count ?? 0),
    error: raw.error ?? null,
    deploymentColour: raw.deployment_colour ?? null,
    applicationVersion: raw.application_version ?? null,
    qaConfig: (raw.qa_config ?? null) as Record<string, unknown> | null,
    producedTx: ((raw.produced_tx ?? []) as Record<string, any>[]).map(producedFromWire),
    ingestReport: ingestReportFromWire(raw.ingest_report),
  };
}

/**
 * The ingest kinds whose parameters are fully recoverable from a run summary
 * (so a run can be rerun as-is). `inline` is excluded: the verbose payloads it
 * was started with are never echoed back by the API, so there's nothing to
 * replay from — inline runs must be re-submitted through the dialog.
 */
const REPRODUCIBLE_SOURCE_KINDS: ReadonlySet<RunSourceKind> = new Set<RunSourceKind>([
  "latest",
  "tx_hashes",
  "tx_specs",
  "block_range",
  "pair_swaps",
]);

/** Whether {@link runToCreateInput} can reproduce this run (see {@link REPRODUCIBLE_SOURCE_KINDS}). */
export function canRerun(job: RunJobSummary): boolean {
  return REPRODUCIBLE_SOURCE_KINDS.has(job.sourceKind);
}

/**
 * Build a create request that reproduces an existing run's parameters — the
 * "Rerun" action. Only valid for {@link canRerun} runs; callers must gate on it
 * (an `inline` run's payloads aren't recoverable from its summary).
 */
export function runToCreateInput(job: RunJobSummary): CreateRunInput {
  return {
    pipelineId: job.pipelineId || undefined,
    source: {
      kind: job.sourceKind,
      chainId: job.chainId,
      provider: job.provider,
      count: job.count,
      address: job.address,
      selector: job.selector,
      onlyMatchingSelector: job.onlyMatchingSelector,
      transactionTypes: job.transactionTypes,
      txHashes: job.txHashes,
      txSpecs: job.txSpecs,
      fromBlock: job.fromBlock,
      toBlock: job.toBlock,
      fromDate: job.fromDate,
      toDate: job.toDate,
    },
    targetTier: job.targetTier,
    storeBody: job.storeBody,
    bodyScope: job.bodyScope,
    overwrite: job.overwrite,
  };
}

function sourceToWire(source: RunSourceInput): Record<string, unknown> {
  const wire: Record<string, unknown> = { kind: source.kind };
  if (source.chainId != null && source.chainId !== "") wire.chain_id = source.chainId;
  if (source.provider) wire.provider = source.provider;
  if (source.count != null) wire.count = source.count;
  if (source.payloads != null) wire.payloads = source.payloads;
  if (source.address != null && source.address !== "") wire.address = source.address;
  if (source.selector != null && source.selector !== "") wire.selector = source.selector;
  if (source.onlyMatchingSelector != null) wire.only_matching_selector = source.onlyMatchingSelector;
  if (source.transactionTypes != null && source.transactionTypes !== "")
    wire.transaction_types = source.transactionTypes;
  if (source.txHashes != null) wire.tx_hashes = source.txHashes;
  if (source.txSpecs != null)
    wire.tx_specs = source.txSpecs.map((s) => ({ chain_id: s.chainId, tx_hash: s.txHash }));
  if (source.fromBlock != null) wire.from_block = source.fromBlock;
  if (source.toBlock != null) wire.to_block = source.toBlock;
  if (source.fromDate != null && source.fromDate !== "") wire.from_date = source.fromDate;
  if (source.toDate != null && source.toDate !== "") wire.to_date = source.toDate;
  if (source.windowMode) wire.window_mode = source.windowMode;
  return wire;
}

export function createApiRunsSource(client: ApiClient): RunsSource {
  const runPath = (id: string) => `/runs/${encodeURIComponent(id)}`;

  // Run status is live (a worker mutates it out-of-band), so reads must always
  // hit the network — the 30s GET cache would otherwise freeze the poll loop on
  // a stale "queued" snapshot and make Refresh a no-op. `cacheTtlMs: 0` opts
  // these reads out of caching.
  const get = async (id: string): Promise<RunJobSummary> =>
    jobFromWire(unwrapData(await client.get<Record<string, any>>(runPath(id), { cacheTtlMs: 0 })));

  return {
    async list(query?: ListRunsQuery): Promise<RunJobPage> {
      const page = unwrapData(
        await client.get<{ items?: Record<string, any>[]; next_cursor?: string | null }>("/runs", {
          query: {
            pipeline_id: query?.pipelineId,
            cursor: query?.cursor ?? undefined,
            limit: query?.limit,
          },
          cacheTtlMs: 0,
        }),
      );
      return {
        items: (page?.items ?? []).map(jobFromWire),
        nextCursor: page?.next_cursor ?? null,
      };
    },
    get,
    poll: get,
    async create(input: CreateRunInput): Promise<RunJobSummary> {
      const body: Record<string, unknown> = { source: sourceToWire(input.source) };
      if (input.pipelineId) body.pipeline_id = input.pipelineId;
      if (input.targetTier != null) body.target_tier = input.targetTier;
      if (input.storeBody != null) body.store_body = input.storeBody;
      if (input.bodyScope != null) body.body_scope = input.bodyScope;
      if (input.overwrite != null) body.overwrite = input.overwrite;
      const created = jobFromWire(unwrapData(await client.post<Record<string, any>>("/runs", body)));
      client.invalidate("/runs");
      return created;
    },
  };
}
